export const DELIVERY_MODES = [
  "immediate",
  "summary",
  "in_app_only",
  "off",
];

export const CATEGORY_CONFIG = [
  {
    id: "direct_messages",
    titleKey: "notification_category_direct_messages",
    descriptionKey: "notification_category_direct_messages_description",
  },
  {
    id: "friend_requests",
    titleKey: "notification_category_friend_requests",
    descriptionKey: "notification_category_friend_requests_description",
  },
  {
    id: "replies_and_mentions",
    titleKey: "notification_category_replies_and_mentions",
    descriptionKey: "notification_category_replies_and_mentions_description",
  },
  {
    id: "social_activity",
    titleKey: "notification_category_social_activity",
    descriptionKey: "notification_category_social_activity_description",
  },
  {
    id: "group_actions",
    titleKey: "notification_category_group_actions",
    descriptionKey: "notification_category_group_actions_description",
  },
  {
    id: "trip_and_location",
    titleKey: "notification_category_trip_and_location",
    descriptionKey: "notification_category_trip_and_location_description",
  },
  {
    id: "safety_and_trip_alerts",
    titleKey: "notification_category_safety_and_trip_alerts",
    descriptionKey: "notification_category_safety_and_trip_alerts_description",
  },
  {
    id: "competitions_and_updates",
    titleKey: "notification_category_competitions_and_updates",
    descriptionKey: "notification_category_competitions_and_updates_description",
  },
];

export const CATEGORY_CONFIG_BY_ID = Object.fromEntries(
  CATEGORY_CONFIG.map((category) => [category.id, category])
);

export const MODE_CONFIG = {
  immediate: {
    labelKey: "notification_mode_immediate",
    descriptionKey: "notification_mode_immediate_description",
  },
  summary: {
    labelKey: "notification_mode_summary",
    descriptionKey: "notification_mode_summary_description",
  },
  in_app_only: {
    labelKey: "notification_mode_in_app_only",
    descriptionKey: "notification_mode_in_app_only_description",
  },
  off: {
    labelKey: "notification_mode_off",
    descriptionKey: "notification_mode_off_description",
  },
};

export const NOTIFICATION_LOCALES = [
  "en",
  "bg",
  "el",
  "es",
  "fr",
  "pt",
  "pl",
  "de",
  "tr",
];

export const ERROR_TRANSLATION_KEYS = {
  notification_preferences_unavailable: "notification_preferences_save_unavailable",
  notification_preferences_invalid: "notification_preferences_save_invalid",
  notification_timezone_invalid: "notification_timezone_invalid",
  notification_locale_invalid: "notification_language_invalid",
  notification_category_invalid: "notification_category_invalid",
  notification_mode_invalid: "notification_mode_invalid",
  notification_quiet_hours_invalid: "notification_quiet_hours_invalid",
  csrf_token_invalid: "notification_preferences_session_error",
};

const TIME_PATTERN = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

export function clonePreferences(preferences) {
  return preferences ? JSON.parse(JSON.stringify(preferences)) : null;
}

export function orderedKnownCategories(categories = []) {
  return categories
    .filter((category) => CATEGORY_CONFIG_BY_ID[category])
    .map((category) => CATEGORY_CONFIG_BY_ID[category]);
}

export function supportedReturnedModes(modes = []) {
  return modes.filter((mode) => MODE_CONFIG[mode]);
}

export function isStrictTime(value) {
  return typeof value === "string" && TIME_PATTERN.test(value);
}

export function validatePreferenceDraft(draft, availableModes = DELIVERY_MODES) {
  if (!draft) return "notification_preferences_invalid";
  const quietHours = draft.quietHours || {};
  if (!isStrictTime(quietHours.start) || !isStrictTime(quietHours.end)) {
    return "notification_quiet_hours_invalid";
  }
  if (quietHours.start === quietHours.end) {
    return "notification_quiet_hours_invalid";
  }
  if (!NOTIFICATION_LOCALES.includes(draft.locale)) {
    return "notification_locale_invalid";
  }
  const supportedModes = new Set(availableModes.filter((mode) => MODE_CONFIG[mode]));
  for (const category of CATEGORY_CONFIG) {
    if (!supportedModes.has(draft.categoryModes?.[category.id])) {
      return "notification_mode_invalid";
    }
  }
  return null;
}

export function buildPreferencePatch(saved, draft) {
  if (!saved || !draft) return {};
  const patch = {};

  if (saved.previewsEnabled !== draft.previewsEnabled) {
    patch.previewsEnabled = draft.previewsEnabled;
  }
  if (saved.locale !== draft.locale) {
    patch.locale = draft.locale;
  }

  const quietHours = {};
  for (const field of ["enabled", "start", "end", "timezone"]) {
    if (saved.quietHours?.[field] !== draft.quietHours?.[field]) {
      quietHours[field] = draft.quietHours?.[field] ?? null;
    }
  }
  if (Object.keys(quietHours).length) patch.quietHours = quietHours;

  const categoryModes = {};
  for (const category of CATEGORY_CONFIG) {
    const categoryId = category.id;
    if (saved.categoryModes?.[categoryId] !== draft.categoryModes?.[categoryId]) {
      categoryModes[categoryId] = draft.categoryModes?.[categoryId];
    }
  }
  if (Object.keys(categoryModes).length) patch.categoryModes = categoryModes;

  return patch;
}

export function detectDeviceTimezone(intl) {
  try {
    const source = intl === undefined && typeof Intl !== "undefined" ? Intl : intl;
    const timezone = source?.DateTimeFormat?.().resolvedOptions?.().timeZone;
    if (typeof timezone !== "string") return null;
    const normalized = timezone.trim();
    if (!normalized || (!normalized.includes("/") && normalized !== "UTC")) {
      return null;
    }
    return normalized;
  } catch {
    return null;
  }
}
